import { useId } from "react";
import Button from "./Button";

interface Props {
  /** 整组的可见标签（展会日期 / 活动日期…） */
  label: string;
  /** 行值：`YYYY-MM-DD`，**允许 `''`**（这一行还没选） */
  value: string[];
  /** 每次增 / 删 / 改行都回调整份行值（受控，不做局部合并） */
  onChange: (rows: string[]) => void;
  /**
   * testid 前缀，四个 id 由它派生（调用方的冻结清单认的是这四个）：
   * 容器 `{prefix}-dates` · 每行输入框 `{prefix}-date`（多个）·
   * 添加 `{prefix}-date-add` · 删除 `{prefix}-date-remove`（多个）。
   */
  testIdPrefix: string;
  addLabel?: string;
  removeLabel?: string;
}

/**
 * 日期**行列表**（展会日期这类"一天一行"的字段）。
 *
 * ## 为什么是 `<input type="date">` 而不是文字输入
 *
 * 文字输入要求用户背格式（`2026-08-15,2026-08-16`），而写歪的代价不是本地提示：
 * 服务端 `@IsDateString()` 直接 400，结算侧还要求严格的 `YYYY-MM-DD`（否则 422）。
 * 原生日期选择器只会产出合法日历日或空串，格式问题在控件这一层就不存在了。
 *
 * ## 值模型：受控 + **允许空串的行**
 *
 * `value` 是"行"而不是"已选日期"——否则"加了一行但还没选"这件事无处可放，
 * 只能退化成组件内部 state + 与外部 props 同步（`useEffect` 回写，编辑模式回填时容易打架）。
 * 空行的清理交给 `normalizeDates()`（`dates.ts`），提交前调一次即可。
 *
 * ## 每行的 `<button>` 都必须是 `type="button"`
 *
 * 本组件用在 `<form>` 里，`<button>` 的默认 `type` 是 `submit`：漏掉这一条，
 * 点"添加日期"会直接把表单提交出去（而症状是"点一下就开始创建展会了"）。
 *
 * ## 可访问名
 *
 * 整组用 `role="group"` + `aria-labelledby` 指向可见标签（`useId()` 生成 id，
 * 同页多个日期字段不会撞车）；每行输入框另有 `aria-label="{label} {n}"`——
 * 可见文字是它的前缀，满足 WCAG 2.5.3 的"可访问名包含可见标签"。
 *
 * ## 索引做 key
 *
 * 行只有"尾部追加"与"按下标删除"两种变更，没有重排；每行也没有内部 state，
 * 因此索引 key 不会带来"值跟着错误的行跑"的问题。
 */
export default function DateListField({
  label,
  value,
  onChange,
  testIdPrefix,
  addLabel = "添加日期",
  removeLabel = "删除",
}: Props) {
  const labelId = useId();
  // 兜底不变量：至少一行。`value` 为空时按"一行空行"渲染，赋值也写回这一行。
  const rows = value.length === 0 ? [""] : value;

  function setRow(index: number, day: string): void {
    onChange(rows.map((row, i) => (i === index ? day : row)));
  }

  function addRow(): void {
    onChange([...rows, ""]);
  }

  function removeRow(index: number): void {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`${testIdPrefix}-dates`}>
      <span id={labelId} className="text-sm font-bold">
        {label}
      </span>
      <div role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
        {rows.map((row, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="date"
              aria-label={`${label} ${index + 1}`}
              data-testid={`${testIdPrefix}-date`}
              value={row}
              onChange={(event) => setRow(index, event.target.value)}
              className="min-h-[44px] min-w-0 flex-1 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
            />
            {rows.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="danger"
                data-testid={`${testIdPrefix}-date-remove`}
                onClick={() => removeRow(index)}
              >
                {removeLabel}
              </Button>
            )}
          </div>
        ))}
      </div>
      <div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid={`${testIdPrefix}-date-add`}
          onClick={addRow}
        >
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
